import { Result } from 'ts-res';
import { KameleoonError } from 'src/kameleoonError';
import { SegmentType, TargetingDataType, Tree } from 'src/targeting';
import { DataProcessor } from './dataProcessor';
import { CustomData } from './customData';
import {
  BrowserDataType,
  CustomDataType,
  DataFilterType,
  DataManagerParametersType,
  DeviceDataType,
  GeolocationDataType,
  KameleoonData,
  KameleoonDataType,
  KameleoonVisitorDataType,
  MutUpdateTargetingDataParametersType,
  OperatingSystemDataType,
  PageViewDataType,
  ProcessCustomDataManagerParametersType,
  TargetedSegmentDataType,
  VisitorsDataType,
} from './types';
import { IStorage, VISIT_DURATION } from 'src/storage';
import {
  CustomDataConfigurationType,
  CustomDataScope,
} from 'src/clientConfiguration';
import { Utilities } from 'src/utilities';
import { DataInfoType, MappingIdentifierType } from 'src/storage/types';
import { Conversion } from './conversion';
import { PageView } from './pageView';
import { Browser } from './browser';
import { Device } from './device';
import { GeolocationData } from './geolocation';
import { TrackingStatus } from 'src/types';
import { ExternalPackageInfoType } from 'src/sdkInfoTypes';
import { OperatingSystem } from './operatingSystem';
import { KameleoonLogger } from 'src/logging';
import { StaticData } from 'src/kameleoonData/staticData';
import { TargetedSegment } from 'src/kameleoonData/targetedSegment';

export interface IDataManager {
  storeData(data: VisitorsDataType): Result<void, KameleoonError>;
  storeTrackedData(data: VisitorsDataType): void;
  getTree(segment: SegmentType): Tree;
  clearTrees(): void;
  isPersistentCustomData(dataItem: KameleoonDataType): dataItem is CustomData;
  isUniqueIdentifier(visitorCode: string): boolean;
  getLinkedVisitor(visitorCode: string, index: number | null): string | null;
  getVisitorData(visitorCode: string): KameleoonVisitorDataType | undefined;
  getVisitorIdentifier(visitorCode: string): string;
  getPendingData(visitorCode: string): KameleoonDataType[];
  getUnsentData(visitorCode: string): (KameleoonDataType | StaticData)[];
  getVisitorStoredDataTypes(visitorCode: string): Set<KameleoonData>;
  readonly identifierCustomDataIndex: number | null;
  readonly storedCustomDataIndexes: Set<number>;
  readonly trees: Map<string, Tree>;
  readonly unsentDataVisitors: string[];
}

export class DataManager implements IDataManager {
  protected dataStorage: IStorage<TargetingDataType>;
  protected infoStorage: IStorage<DataInfoType>;
  protected dataProcessor: DataProcessor;
  protected targetingTrees: Map<string, Tree> = new Map();
  protected mappingIdentifierCustomDataIndex: number | null = null;
  protected persistentCustomDataIndexes: Set<number> = new Set();
  protected localCustomDataIndexes: Set<number> = new Set();
  protected cleanupIntervalId: NodeJS.Timeout | null = null;
  protected packageInfo: ExternalPackageInfoType;

  constructor({
    dataStorage,
    infoStorage,
    cleanupInterval,
    packageInfo,
  }: DataManagerParametersType) {
    KameleoonLogger.debug`CALL: new DataManager(dataStorage, infoStorage, cleanupInterval: ${cleanupInterval})`;
    this.dataStorage = dataStorage;
    this.infoStorage = infoStorage;
    this.dataProcessor = new DataProcessor(cleanupInterval, packageInfo);
    this.packageInfo = packageInfo;

    if (cleanupInterval) {
      const cleanupCallback = () => {
        this.cleanupData();
      };

      // --- Note ---
      // We can not make delayed code execution recoverable, as it might lead
      // to targeting data storage size overflow
      try {
        this.cleanupIntervalId = setInterval(cleanupCallback, cleanupInterval);
      } catch (err) {
        if (this.cleanupIntervalId) {
          clearInterval(this.cleanupIntervalId);
        }
        KameleoonLogger.error`Failed to set cleanup interval: ${cleanupInterval}`;
        throw err;
      }
    }
    KameleoonLogger.debug`RETURN: new DataManager(dataStorage, infoStorage, cleanupInterval: ${cleanupInterval})`;
  }

  public getUnsentData(
    visitorCode: string,
  ): (KameleoonDataType | StaticData)[] {
    const infoResult = this.infoStorage.read();

    if (!infoResult.ok) {
      return [];
    }

    const infoData = infoResult.data;
    const targetingData = this.getVisitorData(visitorCode);

    if (!infoData.unsentData[visitorCode] || !targetingData) {
      return [];
    }

    const unsentDataKeys = infoData.unsentData[visitorCode];
    const isNextVisit = infoData.nextVisitTime <= Date.now();

    const filter: DataFilterType = ({ status, key, cdIndex }) => {
      if (typeof cdIndex === 'number') {
        // --- Note ---
        // Persistent CustomData is mandatory for sending each time
        // but only once per visit
        if (this.persistentCustomDataIndexes.has(cdIndex) && isNextVisit) {
          return true;
        }
      }

      return unsentDataKeys.includes(key) && status === TrackingStatus.Unsent;
    };

    if (isNextVisit) {
      infoData.nextVisitTime = Date.now() + VISIT_DURATION;
      this.infoStorage.write(infoData);
    }

    return this.getDataFromRaw(targetingData, filter);
  }

  public getPendingData(visitorCode: string): KameleoonDataType[] {
    const targetingData = this.getVisitorData(visitorCode);

    if (!targetingData) {
      return [];
    }

    const filter: DataFilterType = ({ status }) =>
      status === TrackingStatus.Pending;

    return this.getDataFromRaw(targetingData, filter);
  }

  private getDataFromRaw(
    targetingData: KameleoonVisitorDataType,
    filter: DataFilterType,
  ): KameleoonDataType[] {
    const resultData: KameleoonDataType[] = [];

    for (const [key, data] of Object.entries(targetingData)) {
      if (!data) {
        continue;
      }

      switch (key) {
        case KameleoonData.CustomData: {
          for (const customData of Object.values(data)) {
            if (!customData) {
              continue;
            }

            const { status, index } = customData;

            if (filter({ status, key, cdIndex: index })) {
              const customDataItem = CustomData._fromRaw(customData);
              resultData.push(customDataItem);
            }
          }

          break;
        }
        case KameleoonData.Conversion: {
          for (const conversion of Object.values(data)) {
            if (!conversion) {
              continue;
            }

            if (filter({ status: conversion.status, key })) {
              const customDataItem = Conversion._fromRaw(conversion);
              resultData.push(customDataItem);
            }
          }

          break;
        }
        case KameleoonData.PageView: {
          const pageViewData = data as { [key: string]: PageViewDataType };

          for (const pageView of Object.values(pageViewData)) {
            if (filter({ status: pageView.status, key })) {
              const pageViewItem = PageView._fromRaw(pageView);
              resultData.push(pageViewItem);
            }
          }

          break;
        }
        case KameleoonData.TargetedSegment: {
          const targetedSegmentData = data as {
            [key: string]: TargetedSegmentDataType;
          };

          for (const targetedSegment of Object.values(targetedSegmentData)) {
            if (filter({ status: targetedSegment.status, key })) {
              const targetedSegmentItem =
                TargetedSegment._fromRaw(targetedSegment);
              resultData.push(targetedSegmentItem);
            }
          }

          break;
        }
        case KameleoonData.GeolocationData: {
          const geolocationData = data as GeolocationDataType;

          if (!data) {
            continue;
          }

          if (filter({ status: geolocationData.status, key })) {
            const geolocationItem = GeolocationData._fromRaw(geolocationData);
            resultData.push(geolocationItem);
          }

          break;
        }
        case KameleoonData.Browser: {
          const browserData = data as BrowserDataType;

          if (filter({ status: browserData.status, key })) {
            const browserItem = Browser._fromRaw(browserData);
            resultData.push(browserItem);
          }

          break;
        }
        case KameleoonData.Device: {
          const deviceData = data as DeviceDataType;

          if (filter({ status: deviceData.status, key })) {
            const deviceItem = Device._fromRaw(deviceData);
            resultData.push(deviceItem);
          }

          break;
        }
        case KameleoonData.OperatingSystem: {
          const osData = data as OperatingSystemDataType;

          if (filter({ status: osData.status, key })) {
            const osItem = OperatingSystem._fromRaw(osData);
            resultData.push(osItem);
          }
        }
      }
    }

    return resultData;
  }

  public storeTrackedData(data: VisitorsDataType): void {
    this.storeData(data);

    const infoResult = this.infoStorage.read();

    if (!infoResult.ok) {
      return;
    }

    const unsentData = infoResult.data.unsentData;

    for (const [visitorCode, kameleoonData] of Object.entries(data)) {
      if (!unsentData[visitorCode]) {
        return;
      }

      const unsentDataKeys = new Set(unsentData[visitorCode]);

      kameleoonData.forEach(({ data }) => {
        if (unsentDataKeys.has(data.type)) {
          unsentDataKeys.delete(data.type);
        }
      });

      const hasPersistentCustomData = kameleoonData.some(({ data }) => {
        if (data.type !== KameleoonData.CustomData) {
          return false;
        }

        const { index } = data as CustomDataType;

        return (
          this.persistentCustomDataIndexes.has(index) ||
          this.mappingIdentifierCustomDataIndex === index
        );
      });

      if (hasPersistentCustomData) {
        unsentDataKeys.add(KameleoonData.CustomData);
      }

      unsentData[visitorCode] = Array.from(unsentDataKeys);

      if (!unsentData[visitorCode].length) {
        delete unsentData[visitorCode];
      }
    }

    this.infoStorage.write(infoResult.data);
  }

  public storeData(data: VisitorsDataType): Result<void, KameleoonError>;
  public storeData(
    visitorCode: string,
    ...data: KameleoonDataType[]
  ): Result<void, KameleoonError>;
  public storeData(
    firstParameter: VisitorsDataType | string,
    ...secondParameter: KameleoonDataType[]
  ): Result<void, KameleoonError> {
    KameleoonLogger.debug`CALL: DataManager.storeData(visitorCode: ${firstParameter}, data: ${secondParameter})`;
    const result = this.dataStorage.read();
    const infoResult = this.infoStorage.read();

    if (!result.ok) {
      return result;
    }

    if (!infoResult.ok) {
      return infoResult;
    }

    const targetingData = result.data;
    const infoData = infoResult.data;

    if (typeof firstParameter === 'string') {
      this.mutUpdateTargetingData({
        infoData,
        targetingData,
        visitorCode: firstParameter,
        kameleoonData: secondParameter!,
      });
    } else {
      for (const [visitorCode, kameleoonData] of Object.entries(
        firstParameter,
      )) {
        this.mutUpdateTargetingData({
          infoData,
          targetingData,
          visitorCode,
          kameleoonData,
        });
      }
    }

    this.cleanupData();
    this.infoStorage.write(infoResult.data);

    const writeResult = this.dataStorage.write(targetingData);
    KameleoonLogger.debug`RETURN: DataManager.storeData(visitorCode: ${firstParameter}, data: ${secondParameter}) -> (writeResult: ${writeResult})`;
    return writeResult;
  }

  public getTree(segment: SegmentType): Tree {
    if (this.targetingTrees.has(segment.id)) {
      return this.targetingTrees.get(segment.id)!;
    }

    const targetingTree = new Tree(segment);
    this.targetingTrees.set(segment.id, targetingTree);

    return targetingTree;
  }

  public clearTrees(): void {
    this.targetingTrees.clear();
  }

  public isPersistentCustomData(
    dataItem: KameleoonDataType,
  ): dataItem is CustomData {
    if (!this.persistentCustomDataIndexes.size) {
      return false;
    }

    if (Utilities.isCustomData(dataItem)) {
      return this.persistentCustomDataIndexes.has(dataItem.data.index);
    }

    return false;
  }

  public isUniqueIdentifier(visitorCode: string): boolean {
    const targetingData = this.getVisitorData(visitorCode);
    const uniqueIdentifier = targetingData?.[KameleoonData.UniqueIdentifier];

    return uniqueIdentifier?.value ?? false;
  }

  public getVisitorIdentifier(visitorCode: string): string {
    const infoResult = this.infoStorage.read();

    if (!infoResult.ok) {
      return visitorCode;
    }

    const infoData = infoResult.data;

    infoData.mappingIdentifiers ??= {};

    return (
      infoData.mappingIdentifiers[visitorCode]?.mappingIdentifier ?? visitorCode
    );
  }

  public getMappingIdentifier(
    visitorCode: string,
  ): MappingIdentifierType | undefined {
    const infoResult = this.infoStorage.read();
    if (!infoResult.ok) {
      return undefined;
    }
    const infoData = infoResult.data;
    if (infoData.mappingIdentifiers) {
      return infoData.mappingIdentifiers[visitorCode];
    }
    return undefined;
  }

  public setMappingIdentifier(
    visitorCode: string,
    mappingIdentifier: MappingIdentifierType,
  ): void {
    const infoResult = this.infoStorage.read();

    if (!infoResult.ok) {
      return;
    }

    const infoData = infoResult.data;

    infoData.mappingIdentifiers ??= {};

    const currentMappingIdentifier = infoData.mappingIdentifiers[visitorCode];
    if (
      !currentMappingIdentifier ||
      currentMappingIdentifier.timestamp < mappingIdentifier.timestamp
    ) {
      infoData.mappingIdentifiers[visitorCode] = mappingIdentifier;
      this.infoStorage.write(infoData);
    }
  }

  public getVisitorData(
    visitorCode: string,
  ): KameleoonVisitorDataType | undefined {
    this.cleanupData();

    const storageData = this.dataStorage.read();

    if (!storageData.ok) {
      return undefined;
    }

    let { visitorReference, data, isReference } =
      this.dataProcessor.dereferenceData(storageData.data, visitorCode);

    if (this.packageInfo.isServer && isReference && !data[visitorReference]) {
      const infoData = this.infoStorage.read();

      if (infoData.ok && infoData.data.mappingIdentifiers) {
        delete infoData.data.mappingIdentifiers[visitorCode];
      }
      delete data[visitorCode];
      visitorReference = visitorCode;
    }

    return data[visitorReference];
  }

  public getVisitorStoredDataTypes(visitorCode: string): Set<KameleoonData> {
    const targetingData = this.getVisitorData(visitorCode);

    if (!targetingData) {
      return new Set();
    }

    return new Set(Object.keys(targetingData) as KameleoonData[]);
  }

  public getLinkedVisitor(visitorCode: string): string | null {
    const storageData = this.dataStorage.read();

    if (!storageData.ok) {
      return null;
    }

    if (typeof storageData.data[visitorCode] === 'string') {
      return storageData.data[visitorCode] as string;
    }

    if (this.mappingIdentifierCustomDataIndex === null) {
      return null;
    }

    const visitorData = storageData.data[
      visitorCode
    ] as KameleoonVisitorDataType;

    if (!visitorData) {
      return null;
    }

    const customData = visitorData[KameleoonData.CustomData];

    if (customData && customData[this.mappingIdentifierCustomDataIndex]) {
      return (
        customData[this.mappingIdentifierCustomDataIndex] as CustomDataType
      ).value[0];
    }

    return null;
  }

  private mutUpdateTargetingData({
    infoData,
    visitorCode,
    kameleoonData,
    targetingData,
  }: MutUpdateTargetingDataParametersType) {
    for (const dataItem of kameleoonData) {
      if (dataItem.data.type === KameleoonData.CustomData) {
        this.processCustomData({
          infoData,
          customData: dataItem as CustomData,
          mutData: targetingData,
          visitorCode,
        });
      }

      const expirationTime = this.dataProcessor.mutUpdateData({
        infoData,
        visitorCode,
        mutData: targetingData,
        dataItem,
      });

      const nextCleanup = infoData.nextDataCleanup;

      if (!nextCleanup || (nextCleanup && expirationTime < nextCleanup)) {
        infoData.nextDataCleanup = expirationTime;
      }

      if (dataItem.data.status === TrackingStatus.Unsent) {
        this.dataProcessor.mutAddUnsentData({
          mutData: infoData,
          visitorCode,
          dataType: dataItem.data.type,
        });
      }
    }
  }

  private cleanupData(): void {
    const result = this.dataStorage.read();
    const infoResult = this.infoStorage.read();

    if (!result.ok || !infoResult.ok) {
      return;
    }

    const data = result.data;
    const infoData = infoResult.data;

    const nextCleanup = infoData.nextDataCleanup;

    if (nextCleanup && nextCleanup < Date.now()) {
      const nextCleanupTime = this.dataProcessor.mutCleanupData(data, infoData);

      infoData.nextDataCleanup = nextCleanupTime || undefined;

      this.infoStorage.write(infoData);
      this.dataStorage.write(data);
    }
  }

  private processCustomData({
    infoData,
    mutData,
    customData,
    visitorCode,
  }: ProcessCustomDataManagerParametersType): void {
    const { data } = customData;

    const isDataValid = Boolean(data.value.length && data.value[0].length);

    if (this.mappingIdentifierCustomDataIndex === data.index && isDataValid) {
      customData._isMappingIdentifier = true;

      const userId = data.value[0];
      if (visitorCode !== userId) {
        KameleoonLogger.info`Linked anonymous visitor ${visitorCode} with user ${userId}`;
        // --- Note ---
        // Associate linked visitor codes
        this.dataProcessor.createReference({
          infoData,
          mutData,
          visitorCode,
          linkedVisitor: data.value[0],
        });

        infoData.mappingIdentifiers ??= {};

        infoData.mappingIdentifiers[userId] = {
          mappingIdentifier: visitorCode,
          timestamp: Date.now(),
        };
      }
    }

    if (this.localCustomDataIndexes.has(data.index)) {
      customData.status = TrackingStatus.Sent;
    }
  }

  get unsentDataVisitors(): string[] {
    const infoResult = this.infoStorage.read();

    if (!infoResult.ok) {
      return [];
    }

    return Object.keys(infoResult.data.unsentData);
  }

  get identifierCustomDataIndex(): number | null {
    return this.mappingIdentifierCustomDataIndex;
  }

  get trees(): Map<string, Tree> {
    return this.targetingTrees;
  }

  get storedCustomDataIndexes(): Set<number> {
    const result = this.dataStorage.read();

    if (!result.ok) {
      return new Set();
    }

    const targetingData = result.data;

    if (!(KameleoonData.CustomData in targetingData)) {
      return new Set();
    }

    const indexes = new Set<number>();

    for (const index in Object.keys(targetingData[KameleoonData.CustomData])) {
      indexes.add(Number(index));
    }

    return indexes;
  }

  set customDataIndexes(customData: CustomDataConfigurationType[]) {
    const [customDataLocalOnlyIndexes, persistentCustomDataIndexes]: Array<
      number[]
    > = [[], []];

    customData.forEach((customData) => {
      if (customData.localOnly) {
        customDataLocalOnlyIndexes.push(customData.index);
      }

      if (customData.scope === CustomDataScope.Visitor) {
        persistentCustomDataIndexes.push(customData.index);
      }
    });

    this.mappingIdentifierCustomDataIndex =
      customData.find((customData) => customData.isMappingIdentifier)?.index ||
      null;

    this.localCustomDataIndexes = new Set(customDataLocalOnlyIndexes);
    this.persistentCustomDataIndexes = new Set(persistentCustomDataIndexes);
  }
}
